import { secureApiService } from './secureApiService';
import { secureTokenStorage } from './secureTokenStorage';

class AdminApiService {
  constructor() {
    this.loginEndpoint = '/api/auth/login';
    
    // Static credentials as specified
    this.staticCredentials = {
      email: "admin@memicai.com",
      password: "Aa123456",
      device_token: "123456",
      device_type: "ios"
    };
  }

  // Admin login API call with enhanced security and CORS handling
  async adminLogin() {
    try {
      console.log('🔐 Admin login API call started', this.staticCredentials, this.loginEndpoint);
      
      // Test CORS connectivity first in development
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Testing CORS connectivity...');
        const corsTest = await secureApiService.testCorsConnectivity();
        console.log('🔍 CORS test result:', corsTest);
      }
      
      const response = await secureApiService.post(this.loginEndpoint, this.staticCredentials);
      
      // Handle CORS errors specifically
      if (response.corsError) {
        console.error('🚫 CORS Error during admin login:', response);
        return {
          success: false,
          error: 'Cannot connect to API server due to CORS policy. The server needs to be configured to allow requests from this domain.',
          corsError: true,
          details: response.details
        };
      }
      
      if (response.success && response.data && response.data.data && response.data.data.user) {
        const userData = response.data.data.user;
        const apiToken = userData.api_token;
        
        if (apiToken) {
          // Store the API token and user data securely with encryption
          const tokenStored = secureTokenStorage.setApiToken(apiToken);
          const userStored = secureTokenStorage.setUserData(userData);
          
          if (tokenStored && userStored) {
            return {
              success: true,
              user: userData,
              token: apiToken
            };
          } else {
            return {
              success: false,
              error: 'Failed to securely store authentication data'
            };
          }
        } else {
          return {
            success: false,
            error: 'No API token received from server'
          };
        }
      } else {
        return {
          success: false,
          error: response.error || 'Invalid response from server'
        };
      }
    } catch (error) {
      console.error('Admin login API call failed:', error);
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  }

  // Get current admin data from secure storage
  getCurrentAdminData() {
    return secureTokenStorage.getUserData();
  }

  // Check if admin has valid API token with security validation
  hasValidSession() {
    return secureTokenStorage.hasValidToken();
  }

  // Clear admin session securely
  clearSession() {
    return secureTokenStorage.clearAll();
  }

  // Get API token for other requests
  getApiToken() {
    return secureTokenStorage.getApiToken();
  }

  // Get authorization headers for API requests
  getAuthHeaders() {
    return secureTokenStorage.getSecurityHeaders();
  }

  // Check if token needs refresh
  needsTokenRefresh() {
    return secureTokenStorage.needsRefresh();
  }

  // Get time until token expires
  getTokenTimeToExpiry() {
    return secureTokenStorage.getTokenTimeToExpiry();
  }

  // Make authenticated API request with enhanced security
  async makeAuthenticatedRequest(endpoint, method = 'GET', body = null) {
    // Check if token is still valid
    if (!this.hasValidSession()) {
      return {
        success: false,
        error: 'No valid authentication session available'
      };
    }

    // Check if token needs refresh
    if (this.needsTokenRefresh()) {
      console.warn('Token expires soon. Consider implementing token refresh.');
    }

    try {
      switch (method.toUpperCase()) {
        case 'POST':
          return await secureApiService.post(endpoint, body);
        case 'PUT':
          return await secureApiService.put(endpoint, body);
        case 'DELETE':
          return await secureApiService.delete(endpoint);
        case 'GET':
        default:
          return await secureApiService.get(endpoint);
      }
    } catch (error) {
      // Handle authentication errors
      if (error.message.includes('Authentication expired')) {
        this.clearSession();
      }
      
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get security statistics
  getSecurityStats() {
    return {
      ...secureApiService.getSecurityStats(),
      tokenExpiry: this.getTokenTimeToExpiry(),
      needsRefresh: this.needsTokenRefresh(),
      hasValidSession: this.hasValidSession()
    };
  }
}

export const adminApiService = new AdminApiService();
export default adminApiService; 